import { 
  collection, 
  addDoc, 
  doc, 
  getDocs,
  getDoc,
  setDoc,
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
  DocumentData,
  QuerySnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Conversation, Message } from '../types';
import { DefaultSettingsService } from './defaultSettingsService';

export class ConversationService {
  private static conversationsCollection = 'conversations';
  private static messagesCollection = 'messages';

  /**
   * Create a new conversation for a user
   */
  static async createConversation(
    userId: string,
    title?: string,
    initialTargetCount?: number,
    initialReasoningEffort?: 'default' | 'low' | 'medium' | 'high',
    initialPrioritize?: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom',
    initialSelectedModels?: string[],
    batchRunID?: string,
    hmaicbTimestamp?: string,
    published?: boolean
  ): Promise<string> {
    try {
      // Fetch configurable defaults from Firestore if not explicitly provided
      const defaults = await DefaultSettingsService.getDefaults();
      
      const conversationData: any = {
        userId,
        title: title || 'New Conversation',
        createdAt: serverTimestamp(),
        lastUpdatedAt: serverTimestamp(),
        active: true, // New conversations are active by default
        modelParameters: {
          targetCount: initialTargetCount ?? defaults.defaultAIs, // Use provided value or configurable default
          reasoningEffort: initialReasoningEffort ?? 'default', // Default to 'default' (no reasoning object)
          prioritize: initialPrioritize ?? defaults.defaultPrioritize, // Use provided value or configurable default
          selectedModels: initialSelectedModels ?? [] // Save the selected models
        }
      };
      
      // Add batchRunID if provided (for batch test tracking)
      if (batchRunID) {
        conversationData.batchRunID = batchRunID;
      }
      
      // Add hmaicbTimestamp if provided (for human-meets-ai-chatbot tracking)
      if (hmaicbTimestamp) {
        conversationData.hmaicbTimestamp = hmaicbTimestamp;
      }
      
      // Add published flag if provided (for published conversations feature)
      if (published !== undefined) {
        conversationData.published = published;
      }

      const docRef = await addDoc(collection(db, this.conversationsCollection), conversationData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  /**
   * Add a message to a conversation
   */
  static async addMessage(
    conversationId: string, 
    message: Omit<Message, 'id'> | Omit<Message, 'id' | 'timestamp'>
  ): Promise<string> {
    try {
      const messageData: any = {
        ...message,
        // Use provided timestamp if available, otherwise use server timestamp
        timestamp: 'timestamp' in message && message.timestamp 
          ? message.timestamp 
          : serverTimestamp()
      };

      // Remove undefined fields to avoid Firestore errors
      Object.keys(messageData).forEach(key => {
        if (messageData[key] === undefined) {
          delete messageData[key];
        }
      });

      const docRef = await addDoc(
        collection(db, this.conversationsCollection, conversationId, this.messagesCollection),
        messageData
      );
      return docRef.id;
    } catch (error) {
      console.error('Error adding message:', error);
      throw error;
    }
  }

  /**
   * Get all conversations for a user
   */
  static async getConversations(userId: string): Promise<Conversation[]> {
    try {
      const q = query(
        collection(db, this.conversationsCollection),
        where('userId', '==', userId),
        orderBy('lastUpdatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        lastUpdatedAt: doc.data().lastUpdatedAt?.toDate() || new Date()
      })) as Conversation[];
    } catch (error) {
      console.error('Error getting conversations:', error);
      throw error;
    }
  }

  /**
   * Listen to a conversation document in real-time
   */
  static subscribeToConversation(
    conversationId: string,
    callback: (conversationData: DocumentData | undefined) => void
  ): () => void {
    console.log('Setting up listener for conversation document:', conversationId);
    const conversationRef = doc(db, this.conversationsCollection, conversationId);

    let isUnsubscribed = false;
    const unsubscribe = onSnapshot(conversationRef, (docSnapshot) => {
      if (isUnsubscribed) return;
      try {
        if (docSnapshot.exists()) {
          callback(docSnapshot.data());
        } else {
          callback(undefined);
        }
      } catch (error) {
        // Silently handle internal assertion errors - they're often non-fatal
        if (error instanceof Error && error.message.includes('INTERNAL ASSERTION')) {
          console.warn('Firestore internal assertion (non-fatal):', error.message);
        } else {
          console.error('Error in conversation listener callback:', error);
        }
      }
    }, (error) => {
      // Silently handle internal assertion errors - they're often non-fatal
      if (error instanceof Error && error.message.includes('INTERNAL ASSERTION')) {
        console.warn('Firestore internal assertion (non-fatal):', error.message);
      } else {
        console.error('Error listening to conversation:', error);
      }
    });

    return () => {
      isUnsubscribed = true;
      unsubscribe();
    };
  }

  /**
   * Listen to messages in a conversation in real-time
   */
  static subscribeToMessages(
    conversationId: string, 
    callback: (messages: Message[]) => void
  ): () => void {
    console.log('Setting up listener for conversation:', conversationId);
    const messagesRef = collection(db, this.conversationsCollection, conversationId, this.messagesCollection);
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    let isUnsubscribed = false;
    const unsubscribe = onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
      if (isUnsubscribed) return;
      try {
        // console.log('Firestore listener triggered for conversation:', conversationId, 'Docs:', querySnapshot.docs.length);
        const messages: Message[] = querySnapshot.docs.map(doc => {
          const data = doc.data();
          // console.log('Message doc:', doc.id, data);
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date()
          };
        }) as Message[];
        
        // Deduplicate messages by content + timestamp + role to prevent catastrophic duplication
        // This handles cases where Firestore has duplicate documents (e.g., from Cloud Function bugs)
        const seenMessages = new Map<string, Message>(); // key: content+timestamp+role -> message
        const deduplicatedMessages: Message[] = [];
        
        messages.forEach(msg => {
          // Create a unique key based on content, timestamp, role, and message type flags
          // For synthesis messages (isSynthesized=true), use stricter deduplication
          // For grade matrices, use content signature
          const timestamp = msg.timestamp?.getTime() || 0;
          const timestampRounded = Math.floor(timestamp / 1000); // Round to nearest second for tolerance
          
          let key: string;
          if (msg.isSynthesized && !msg.synthesisVariant) {
            // Final ionoff synthesis: deduplicate by content (synthesis can have same timestamp)
            const contentHash = msg.content.substring(0, 200); // First 200 chars should be unique
            key = `synth_final_${msg.role}_${contentHash}`;
          } else if (msg.isGradeMatrix) {
            // Grade matrix: deduplicate by content signature (matrix type + first part of content)
            const matrixType = msg.content.includes('Self-Eval') ? 'selfeval' : 'peer';
            const contentHash = msg.content.substring(0, 150);
            key = `matrix_${matrixType}_${contentHash}`;
          } else if (msg.synthesisVariant) {
            // Synthesis variant: include variant name
            key = `synth_variant_${msg.role}_${timestampRounded}_${msg.synthesisVariant}_${msg.content.substring(0, 100)}`;
          } else {
            // Regular message: use timestamp + content + modelUsed
            // CRITICAL: Include modelUsed to prevent different models with same content from being deduplicated
            const modelId = msg.modelUsed || 'unknown';
            key = `${msg.role}_${modelId}_${timestampRounded}_${msg.content.substring(0, 100)}`;
          }
          
          if (!seenMessages.has(key)) {
            seenMessages.set(key, msg);
            deduplicatedMessages.push(msg);
          } else {
            const existingMsg = seenMessages.get(key)!;
            console.warn('⚠️ Duplicate message detected and filtered out:', {
              existingId: existingMsg.id,
              duplicateId: msg.id,
              role: msg.role,
              isSynthesized: msg.isSynthesized,
              synthesisVariant: msg.synthesisVariant,
              isGradeMatrix: msg.isGradeMatrix,
              contentPreview: msg.content.substring(0, 50),
              timestamp: new Date(timestamp).toISOString(),
              dedupKey: key.substring(0, 100)
            });
          }
        });
        
        if (deduplicatedMessages.length < messages.length) {
          console.warn(`⚠️ Filtered ${messages.length - deduplicatedMessages.length} duplicate messages from Firestore`, {
            originalCount: messages.length,
            deduplicatedCount: deduplicatedMessages.length,
            conversationId: conversationId.substring(0, 20)
          });
        }
        
        // console.log('Processed messages:', deduplicatedMessages);
        callback(deduplicatedMessages);
      } catch (error) {
        // Silently handle internal assertion errors - they're often non-fatal
        if (error instanceof Error && error.message.includes('INTERNAL ASSERTION')) {
          console.warn('Firestore internal assertion (non-fatal):', error.message);
        } else {
          console.error('Error in messages listener callback:', error);
        }
      }
    }, (error) => {
      // Silently handle internal assertion errors - they're often non-fatal
      if (error instanceof Error && error.message.includes('INTERNAL ASSERTION')) {
        console.warn('Firestore internal assertion (non-fatal):', error.message);
      } else {
        console.error('Error listening to messages:', error);
      }
    });

    return () => {
      isUnsubscribed = true;
      unsubscribe();
    };
  }

  /**
   * Update conversation's lastUpdatedAt timestamp
   */
  static async updateConversationTimestamp(conversationId: string): Promise<void> {
    try {
      const conversationRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(conversationRef, {
        lastUpdatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating conversation timestamp:', error);
      throw error;
    }
  }

  /**
   * Get conversation parameters (targetCount, reasoningEffort, prioritize, selectedModels)
   */
  static async getConversationParameters(conversationId: string): Promise<{
    targetCount: number;
    reasoningEffort: 'default' | 'low' | 'medium' | 'high';
    prioritize: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom' | 'cost'; // 'cost' for backward compatibility
    selectedModels?: string[];
  } | null> {
    try {
      const docRef = doc(db, this.conversationsCollection, conversationId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return data.modelParameters || null;
      }
      return null;
    } catch (error) {
      console.error('Error getting conversation parameters:', error);
      return null;
    }
  }

  /**
   * Update conversation's title
   */
  static async updateConversationTitle(conversationId: string, title: string): Promise<void> {
    try {
      const conversationRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(conversationRef, {
        title,
        lastUpdatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating conversation title:', error);
      throw error;
    }
  }

  /**
   * Listen to all conversations for a user in real-time
   * Only returns active conversations (active != false)
   */
  static subscribeToConversations(
    userId: string,
    callback: (conversations: Conversation[]) => void
  ): () => void {
    console.log('Setting up listener for user conversations:', userId);
    const conversationsRef = collection(db, this.conversationsCollection);
    
    // Subscribe to user's own conversations
    const userQuery = query(
      conversationsRef,
      where('userId', '==', userId),
      where('active', '!=', false),
      orderBy('active', 'desc'),
      orderBy('lastUpdatedAt', 'desc')
    );
    
    // Subscribe to batch test conversations
    const batchQuery = query(
      conversationsRef,
      where('userId', '==', 'batch-test-system'),
      where('active', '!=', false),
      orderBy('active', 'desc'),
      orderBy('createdAt', 'desc')
    );
    
    // Store both result sets
    let userConversations: Conversation[] = [];
    let batchConversations: Conversation[] = [];
    
    const mergeAndCallback = () => {
      // Merge and sort by lastUpdatedAt (user convos) or createdAt (batch convos)
      const merged = [...userConversations, ...batchConversations].sort((a, b) => {
        const aTime = a.lastUpdatedAt || a.createdAt;
        const bTime = b.lastUpdatedAt || b.createdAt;
        return bTime.getTime() - aTime.getTime();
      });
      callback(merged);
    };
    
    let isUnsubscribed = false;
    
    // Subscribe to user conversations
    const unsubscribeUser = onSnapshot(userQuery, (querySnapshot: QuerySnapshot<DocumentData>) => {
      if (isUnsubscribed) return;
      try {
        userConversations = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            lastUpdatedAt: data.lastUpdatedAt?.toDate() || new Date()
          };
        }) as Conversation[];
        mergeAndCallback();
      } catch (error) {
        // Silently handle internal assertion errors - they're often non-fatal
        if (error instanceof Error && error.message.includes('INTERNAL ASSERTION')) {
          console.warn('Firestore internal assertion (non-fatal):', error.message);
        } else {
          console.error('Error in user conversations listener callback:', error);
        }
      }
    }, (error) => {
      // Silently handle internal assertion errors - they're often non-fatal
      if (error instanceof Error && error.message.includes('INTERNAL ASSERTION')) {
        console.warn('Firestore internal assertion (non-fatal):', error.message);
      } else {
        console.error('Error listening to user conversations:', error);
      }
    });
    
    // Subscribe to batch test conversations
    const unsubscribeBatch = onSnapshot(batchQuery, (querySnapshot: QuerySnapshot<DocumentData>) => {
      if (isUnsubscribed) return;
      try {
        batchConversations = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            lastUpdatedAt: data.lastUpdatedAt?.toDate() || new Date()
          };
        }) as Conversation[];
        mergeAndCallback();
      } catch (error) {
        // Silently handle internal assertion errors - they're often non-fatal
        if (error instanceof Error && error.message.includes('INTERNAL ASSERTION')) {
          console.warn('Firestore internal assertion (non-fatal):', error.message);
        } else {
          console.error('Error in batch conversations listener callback:', error);
        }
      }
    }, (error) => {
      // Silently handle internal assertion errors - they're often non-fatal
      if (error instanceof Error && error.message.includes('INTERNAL ASSERTION')) {
        console.warn('Firestore internal assertion (non-fatal):', error.message);
      } else {
        console.error('Error listening to batch test conversations:', error);
      }
    });
    
    // Return combined unsubscribe function
    return () => {
      isUnsubscribed = true;
      unsubscribeUser();
      unsubscribeBatch();
    };
  }

  /**
   * Generate a title from the first user message
   */
  static generateTitle(firstMessage: string): string {
    // Take first 50 characters of the message as the title
    const maxLength = 50;
    if (firstMessage.length <= maxLength) {
      return firstMessage;
    }
    return firstMessage.substring(0, maxLength).trim() + '...';
  }

  /**
   * Set cancellation flag for a conversation to stop processing
   */
  static async cancelProcessing(conversationId: string): Promise<void> {
    try {
      const conversationRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(conversationRef, {
        cancelProcessing: true,
        cancelRequestedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error canceling processing:', error);
      throw error;
    }
  }

  /**
   * Clear cancellation flag for a conversation
   */
  static async clearCancellationFlag(conversationId: string): Promise<void> {
    try {
      const conversationRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(conversationRef, {
        cancelProcessing: false,
        cancelRequestedAt: null
      });
    } catch (error) {
      console.error('Error clearing cancellation flag:', error);
      throw error;
    }
  }

  /**
   * Clear processing flag for a conversation
   * This is called by the frontend after all local processing completes
   * to ensure the stop button reliably changes back to send button
   */
  static async clearProcessingFlag(conversationId: string): Promise<void> {
    try {
      const conversationRef = doc(db, this.conversationsCollection, conversationId);
      const conversationSnap = await getDoc(conversationRef);
      
      // Only clear if currently processing (idempotent)
      if (conversationSnap.exists() && conversationSnap.data()?.processing === true) {
        await updateDoc(conversationRef, {
          processing: false,
          lastProcessedAt: serverTimestamp()
        });
        console.log('✅ Frontend cleared processing flag');
      }
    } catch (error) {
      console.error('Error clearing processing flag:', error);
      // Don't throw - this is a cleanup operation that shouldn't break the flow
    }
  }

  /**
   * Publish a conversation (make it publicly readable)
   */
  static async publishConversation(conversationId: string): Promise<void> {
    try {
      const conversationRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(conversationRef, {
        published: true
      });
      console.log('✅ Conversation published:', conversationId);
    } catch (error) {
      console.error('Error publishing conversation:', error);
      throw error;
    }
  }

  /**
   * Unpublish a conversation (make it private again)
   */
  static async unpublishConversation(conversationId: string): Promise<void> {
    try {
      const conversationRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(conversationRef, {
        published: false
      });
      console.log('✅ Conversation unpublished:', conversationId);
    } catch (error) {
      console.error('Error unpublishing conversation:', error);
      throw error;
    }
  }

  /**
   * Fork a published conversation into user's own private copy
   * Returns the new conversation ID
   * 
   * 🎯 FIX: Remap userPromptMessageId references so copied assistant responses
   * correctly point to their new user message IDs (not the old source IDs)
   */
  static async forkConversation(
    sourceConversationId: string,
    newUserId: string
  ): Promise<string> {
    try {
      // 1. Get source conversation
      const sourceRef = doc(db, this.conversationsCollection, sourceConversationId);
      const sourceDoc = await getDoc(sourceRef);
      
      if (!sourceDoc.exists()) {
        throw new Error('Source conversation not found');
      }
      
      const sourceData = sourceDoc.data();
      
      // 2. Create new conversation (unpublished by default)
      const newConversationRef = doc(collection(db, this.conversationsCollection));
      await setDoc(newConversationRef, {
        userId: newUserId,
        title: `Fork: ${sourceData.title || 'Untitled'}`,
        createdAt: serverTimestamp(),
        lastUpdatedAt: serverTimestamp(),
        active: true,
        published: false,
        forkedFrom: sourceConversationId,
        // Copy send config from source
        modelParameters: sourceData.modelParameters || {
          targetCount: 3,
          reasoningEffort: 'default',
          prioritize: 'speed',
          selectedModels: []
        }
      });
      
      // 3. Copy messages (excluding grades, debug, cost summaries)
      const messagesSnapshot = await getDocs(
        collection(db, this.conversationsCollection, sourceConversationId, 'messages')
      );
      
      // 🎯 FIX: Two-pass approach to remap userPromptMessageId references
      // Pass 1: Filter messages and create ID mapping (old ID -> new ID)
      const oldToNewIdMap = new Map<string, string>();
      const messagesToCopy: Array<{oldId: string; newId: string; data: any}> = [];
      
      for (const msgDoc of messagesSnapshot.docs) {
        const msgData = msgDoc.data();
        const oldId = msgDoc.id;
        
        // Skip internal messages (cleanview would hide these anyway)
        if (
          msgData.isGradingResponse ||
          msgData.isExtractionResponse ||
          msgData.isGradeMatrix ||
          msgData.isCostSummary ||
          msgData.isDebugInfo
        ) {
          continue;
        }
        
        // Create new doc ref and track mapping
        const newMsgRef = doc(
          collection(db, this.conversationsCollection, newConversationRef.id, 'messages')
        );
        const newId = newMsgRef.id;
        
        oldToNewIdMap.set(oldId, newId);
        messagesToCopy.push({ oldId, newId, data: msgData });
      }
      
      // Pass 2: Write messages with remapped userPromptMessageId
      const batch = writeBatch(db);
      let copiedCount = 0;
      
      for (const { oldId, newId, data } of messagesToCopy) {
        const newMsgRef = doc(
          db,
          this.conversationsCollection,
          newConversationRef.id,
          'messages',
          newId
        );
        
        // Remap userPromptMessageId if present (for assistant responses)
        const remappedData = { ...data };
        if (data.userPromptMessageId && oldToNewIdMap.has(data.userPromptMessageId)) {
          remappedData.userPromptMessageId = oldToNewIdMap.get(data.userPromptMessageId);
          console.log(`🔗 Remapped userPromptMessageId: ${data.userPromptMessageId} -> ${remappedData.userPromptMessageId}`);
        }
        
        batch.set(newMsgRef, {
          ...remappedData,
          createdAt: serverTimestamp(),
          timestamp: serverTimestamp()
        });
        copiedCount++;
      }
      
      await batch.commit();
      
      console.log(`✅ Conversation forked: ${sourceConversationId} -> ${newConversationRef.id} (${copiedCount} messages copied, ${oldToNewIdMap.size} IDs remapped)`);
      return newConversationRef.id;
    } catch (error) {
      console.error('Error forking conversation:', error);
      throw error;
    }
  }

  /**
   * Right to Privacy: Replace all user content with "right2Bforgotten"
   * - Unpublishes any published conversations first
   * - Replaces conversation titles with "right2Bforgotten"
   * - Replaces message content/text with "right2Bforgotten"
   * - Preserves metadata (grades, timestamps, etc.)
   */
  static async right2Privacy(userId: string): Promise<{ conversationsUpdated: number; messagesUpdated: number }> {
    try {
      console.log('🔒 Starting right2Bforgotten for user:', userId);
      
      // Get all conversations for this user
      const conversationsQuery = query(
        collection(db, this.conversationsCollection),
        where('userId', '==', userId)
      );
      
      const conversationsSnapshot = await getDocs(conversationsQuery);
      
      let conversationsUpdated = 0;
      let messagesUpdated = 0;
      
      // Process each conversation
      for (const conversationDoc of conversationsSnapshot.docs) {
        const conversationId = conversationDoc.id;
        const conversationData = conversationDoc.data();
        
        console.log(`🔒 Processing conversation: ${conversationId} (${conversationData.title})`);
        
        // Update conversation: unpublish if published, replace title
        const conversationRef = doc(db, this.conversationsCollection, conversationId);
        await updateDoc(conversationRef, {
          title: 'right2Bforgotten',
          published: false, // Unpublish first
          lastUpdatedAt: serverTimestamp()
        });
        conversationsUpdated++;
        
        // Get all messages in this conversation
        const messagesSnapshot = await getDocs(
          collection(db, this.conversationsCollection, conversationId, this.messagesCollection)
        );
        
        // Process messages in batches (Firestore limits batch writes to 500)
        const BATCH_SIZE = 450;
        let batch = writeBatch(db);
        let batchCount = 0;
        
        for (const messageDoc of messagesSnapshot.docs) {
          const messageData = messageDoc.data();
          
          // Only replace content for user messages and actual prompt responses
          // that cleanview would display
          
          const isUserMessage = messageData.role === 'user';
          
          // Always replace user messages
          if (isUserMessage) {
            // Fall through to replace content
          }
          // For assistant messages, check if it's a displayable response
          else if (messageData.role === 'assistant') {
            // Skip if backend explicitly marked as non-response
            if (messageData.isPromptResponse === false) {
              continue;
            }
            
            // Skip synthesis variants (intermediate synthesis outputs)
            if (messageData.synthesisVariant) {
              continue;
            }
            
            // Skip internal/meta messages
            if (
              messageData.isGradingResponse ||
              messageData.isExtractionResponse ||
              messageData.isGradeMatrix ||
              messageData.isCostSummary ||
              messageData.isDebugInfo
            ) {
              continue;
            }
            
            // Skip messages that cleanview filters by content patterns
            const content = messageData.content || '';
            if (
              content.includes('**Title Generation') ||
              content.includes('**API Call') ||
              content.includes('**Grading API Call') ||
              content.includes('**Synthesis API Call') ||
              content.includes('Retry Debug') ||
              content.includes('Fallback')
            ) {
              continue;
            }
            
            // Only include if it has modelUsed (actual response) or is final synthesis
            if (!messageData.modelUsed && !messageData.isSynthesized) {
              continue;
            }
          }
          // Skip any other role
          else {
            continue;
          }
          
          // Replace content for qualifying messages
          const messageRef = doc(
            db,
            this.conversationsCollection,
            conversationId,
            this.messagesCollection,
            messageDoc.id
          );
          
          // Replace content field (and text if it exists)
          const updateData: any = {
            content: 'right2Bforgotten'
          };
          
          // Also replace 'text' field if it exists (some messages use this)
          if (messageData.text !== undefined) {
            updateData.text = 'right2Bforgotten';
          }
          
          batch.update(messageRef, updateData);
          batchCount++;
          messagesUpdated++;
          
          // Commit batch if at limit
          if (batchCount >= BATCH_SIZE) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
            console.log(`🔒 Committed batch of ${BATCH_SIZE} message updates`);
          }
        }
        
        
        // Commit remaining messages
        if (batchCount > 0) {
          await batch.commit();
        }
      }
      
      console.log(`✅ Right2Privacy completed: ${conversationsUpdated} conversations, ${messagesUpdated} messages updated`);
      return { conversationsUpdated, messagesUpdated };
    } catch (error) {
      console.error('❌ Error in right2Bforgotten:', error);
      throw error;
    }
  }
}
